import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { organizationApi } from '../services/organization-api'

export interface Organization {
  id: string
  name: string
  slug: string
  role?: string
  business_category?: string
  sample_list_query?: string
}

interface OrgState {
  currentOrg: Organization | null
  orgs: Organization[]
  isLoading: boolean
  /** Indicates that orgs have been fetched/synced at least once this session */
  orgsSynced: boolean
  fetchOrgs: () => Promise<void>
  setCurrentOrg: (org: Organization) => Promise<void>
  setCurrentOrgId: (orgId: string) => void
  clear: () => void
}

export const useOrgStore = create<OrgState>()(
  persist(
    (set, get) => ({
      currentOrg: null,
      orgs: [],
      isLoading: false,
      orgsSynced: false,

      fetchOrgs: async () => {
        set({ isLoading: true })
        try {
          const response = await organizationApi.listMyOrganizations() as any
          const items = response.items || response || []
          const activeOrgId = response.active_organization_id
          const mapped = items.map((row: any) => ({
            id: row.organization_id,
            name: row.organizations?.name || row.organization_id,
            slug: row.organizations?.slug || row.organization_id,
            role: row.role,
            business_category: row.organizations?.business_category ?? undefined,
            sample_list_query: row.organizations?.sample_list_query || undefined,
          }))
          
          // Always prioritize active_organization_id from backend - ignore cached value
          let selected = null
          if (activeOrgId) {
            selected = mapped.find((m: Organization) => m.id === activeOrgId) || null
            if (selected) {
              console.log(`[OrgStore] Using active org from backend: ${selected.name} (${selected.id})`)
            } else {
              console.warn(`[OrgStore] Active org ${activeOrgId} not found in memberships, using first org`)
              selected = mapped[0] || null
            }
          }
          
          // Fallback to first org only if no active org specified
          if (!selected) {
            selected = mapped[0] || null
            if (selected) {
              console.log(`[OrgStore] No active org specified, using first org: ${selected.name} (${selected.id})`)
            }
          }
          
          // Update the backend if currentOrg doesn't match activeOrgId
          if (selected && activeOrgId && selected.id !== activeOrgId) {
            console.log(`[OrgStore] Syncing active org to backend: ${selected.id}`)
            try {
              await organizationApi.setActiveOrganization(selected.id)
            } catch (error) {
              console.error('[OrgStore] Failed to sync active org:', error)
            }
          }
          
          set({ orgs: mapped, currentOrg: selected, isLoading: false, orgsSynced: true })
        } catch (error) {
          console.error('[OrgStore] Error fetching orgs:', error)
          set({ isLoading: false, orgsSynced: true })
        }
      },

      setCurrentOrg: async (org: Organization) => {
        await organizationApi.setActiveOrganization(org.id)
        set({ currentOrg: org })
      },

      setCurrentOrgId: (orgId: string) => {
        const org = get().orgs.find((o) => o.id === orgId)
        if (org) {
          set({ currentOrg: org })
        }
      },

      clear: () => set({ currentOrg: null, orgs: [], orgsSynced: false })
    }),
    {
      name: 'org-storage',
      partialize: (state) => ({
        currentOrg: state.currentOrg,
        orgs: state.orgs
      })
    }
  )
)
